import { DEFAULT_CURRENCY, formatCurrency } from '@configs/currencies';

/**
 * Prices for products-with-media (greeting cards, invitations, menus) in the
 * shop's own currency.
 *
 * DISPLAY ONLY, but it must say what the backend will charge, so this mirrors
 * `resolvePriceForCurrency` in
 * photo-upload-backend/src/core/product-with-media/product-with-media-order.service.ts:
 *
 *   - eur (and no map at all) → the base `price`;
 *   - otherwise the entry for the currency, matched CASE-INSENSITIVELY — the
 *     product_with_media_definition table stores "PLN" while orders and the
 *     shop use "pln";
 *   - an entry that is null/absent falls back to the base NUMBER, charged in
 *     the order's currency. That is what the backend does today, so quoting it
 *     here keeps the editor honest rather than showing a euro figure the
 *     customer will not be charged.
 *
 * The paper surcharge follows the same rule with the paper's own map.
 *
 * Before this the product tile showed "od 1.50 €" and the editor "15.00 €" to a
 * Polish customer who was then charged 65,00 zł.
 */
export type PriceMap = Record<string, number | null | undefined> | null | undefined;

export function resolvePwmPrice(
  base: number | null | undefined,
  perCurrency: PriceMap,
  currency: string | null | undefined,
): number {
  const baseCents = Number(base ?? 0);
  const cur = (currency || DEFAULT_CURRENCY).toLowerCase();
  if (cur === DEFAULT_CURRENCY || !perCurrency) return baseCents;
  for (const [key, value] of Object.entries(perCurrency)) {
    if (key.toLowerCase() === cur && value != null && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return baseCents;
}

/** One unit of the product plus the chosen paper, in minor units of `currency`. */
export function pwmUnitPrice(
  definition: { price?: number | null; priceCurrencies?: PriceMap },
  paper: { price?: number | null; priceCurrencies?: PriceMap } | null | undefined,
  currency: string | null | undefined,
): number {
  const product = resolvePwmPrice(definition.price, definition.priceCurrencies, currency);
  const paperCents = paper ? resolvePwmPrice(paper.price, paper.priceCurrencies, currency) : 0;
  return product + paperCents;
}

/**
 * Format minor units the way the rest of the shop does (cart, checkout):
 * "15,00 €", "65,00 zł", "1 249,00 Kč".
 */
export function formatPwmPrice(cents: number, currency: string | null | undefined): string {
  return formatCurrency((cents ?? 0) / 100, currency || DEFAULT_CURRENCY);
}
